import Swiper from 'swiper';
import { Keyboard, Navigation } from 'swiper/modules';

import { formatPrice } from '../utils/price';
import { slideCounter } from '../utils/slide-counter';

export function contentSingleProduct() {
  // The mobile header and the desktop carousel share the same slider chrome;
  // the carousel's slides keep the original image ratios (auto widths)
  productSlider(document.querySelector('.product-header'));
  productSlider(document.querySelector('article.product .carousel'), {
    slidesPerView: 'auto',
  });

  if (document.querySelector('.component.configurator')) {
    configurator();
    configuratorVisibility();
  }
}

// The floating configurator trigger hides once the product CTA block, with its
// own order button, comes into view (or the footer, on products without one);
// on mobile it also stays hidden until the header's image carousel has scrolled
// out of view (products without images fall back to the header as a whole).
function configuratorVisibility() {
  const element = document.querySelector('.component.configurator');
  const carousel =
    document.querySelector('.product-header .header-carousel') ??
    document.querySelector('.product-header');
  const siteHeader = document.getElementById('site-header');
  const end =
    document.querySelector('.wp-block-product-cta') ?? document.getElementById('site-footer');
  const mobile = window.matchMedia('(width < 48rem)');

  let endReached = false;
  let carouselPassed = false;
  let carouselObserver;

  const apply = () => {
    const visible = !endReached && (!mobile.matches || carouselPassed);

    element.dataset.ctaVisible = String(visible);
  };

  // The site header is fixed, so the carousel counts as out of view once it is
  // behind it rather than past the top of the viewport. Shrinking the root by
  // the header's height puts the crossing on that edge; the header grows with
  // the admin bar and the breakpoint, so this is remeasured on every change.
  const watchCarousel = () => {
    carouselObserver?.disconnect();

    if (!carousel) {
      return;
    }

    const offset = Math.ceil(siteHeader?.getBoundingClientRect().bottom ?? 0);

    carouselObserver = new IntersectionObserver(
      ([entry]) => {
        carouselPassed =
          !entry.isIntersecting && entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0);
        apply();
      },
      { rootMargin: `-${offset}px 0px 0px 0px` },
    );

    carouselObserver.observe(carousel);
  };

  // Past it counts as reached too, as the CTA block is followed by more page.
  // Measured on scroll rather than observed: a jump from below it to above it
  // (Home, a back-to-top link) crosses no intersection and would go unnoticed
  if (end) {
    const measureEnd = () => {
      endReached = end.getBoundingClientRect().top < window.innerHeight;
      apply();
    };

    window.addEventListener('scroll', measureEnd, { passive: true });
    window.addEventListener('resize', measureEnd);
    measureEnd();
  }

  mobile.addEventListener('change', () => {
    watchCarousel();
    apply();
  });

  // Opened from a header color swatch, the configurator shows where the trigger
  // is hidden (configurator.css). On closing, the trigger hides at once instead
  // of fading out: the forced style flush applies the hide with no transition.
  // The observer runs before the next render, so no frame of the fade shows
  const trigger = document.getElementById('open-modal-configurator');

  new MutationObserver(() => {
    if (trigger.getAttribute('aria-expanded') !== 'false') {
      return;
    }

    element.style.transition = 'none';
    element.getBoundingClientRect();
    element.style.transition = '';
  }).observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });

  watchCarousel();
  apply();
}

// Fetches an image into the cache once the page has loaded, so it doesn't
// compete with the images already on show
function preloadImage(src) {
  const preload = () => {
    new Image().src = src;
  };

  if (document.readyState === 'complete') {
    preload();
  } else {
    window.addEventListener('load', preload, { once: true });
  }
}

function productSlider(root, options = {}) {
  const slider = root?.querySelector('.swiper');
  if (!slider) {
    return;
  }

  const swiper = new Swiper(slider, {
    speed: 500,
    loop: slider.querySelectorAll('.swiper-slide').length > 1,
    // Keep the upcoming slide in place before the swipe starts when wrapping around
    loopAdditionalSlides: 1,
    spaceBetween: 8,
    grabCursor: true,
    navigation: {
      nextEl: root.querySelector('.swiper-button-next'),
      prevEl: root.querySelector('.swiper-button-prev'),
    },
    // Swiper's onlyInViewport check compares document coordinates against the
    // viewport, so it never matches a slider further down the page; the
    // IntersectionObserver below gates the keyboard instead
    keyboard: {
      enabled: true,
      onlyInViewport: false,
      pageUpDown: false,
    },
    on: {
      slideChange: slideCounter(root),
    },
    modules: [Keyboard, Navigation],
    ...options,
  });

  new IntersectionObserver(([entry]) => {
    swiper.keyboard[entry.isIntersecting ? 'enable' : 'disable']();
  }).observe(slider);
}

function configurator() {
  const configuratorElement = document.querySelector('.component.configurator');

  // Get the product ID from the data attribute
  const productId = configuratorElement.dataset.productId;
  if (!productId) {
    console.error('No product ID found for configurator');
    return;
  }

  // Config URL from global object
  const configUrl = window.js_localizations?.configurations_url;
  if (!configUrl) {
    console.error('Configuration URL not found in window.js_localizations');
    return;
  }

  // Create attributes state tracking
  // eslint-disable-next-line prefer-const
  let attributeSelections = {};
  let productData = null;
  let attributeOptions = null;

  // Store the attribute order for determining availability
  let attributeOrder = [];

  // Elements for updating
  const orderButton = document.getElementById('order-button');
  const configImage = document.getElementById('config-image');
  const configTitle = document.getElementById('config-title');
  const configDescription = document.getElementById('config-description');
  const priceElement = document.getElementById('config-price');

  // Initialize the product data
  fetchProductData(productId);

  // Main function to fetch the product data
  async function fetchProductData(productId) {
    try {
      // Construct URL with required GET parameters
      const url = `${configUrl}?product_id=${productId}&show_full_variation_info=1`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Process the product data
      if (data.products && data.products[productId]) {
        productData = data.products[productId];
        attributeOptions = data.attribute_options;

        // Store the attribute order
        attributeOrder = productData.attributes;

        setupAttributeHandlers(productData, attributeOptions);
        setupColorOptionHandlers();

        // Initialize attribute selections with the first variation's values
        if (productData.variations && productData.variations.length > 0) {
          selectVariation(productData.variations[0]);
        }
      }

      if (response.ok) {
        document.getElementById('open-modal-configurator').removeAttribute('disabled');
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
    }
  }

  // Setup the attribute option handlers
  function setupAttributeHandlers(product) {
    const attributeElements = configuratorElement.querySelectorAll('.attribute');

    attributeElements.forEach((attributeElement) => {
      const attributeId = attributeElement.dataset.attributeId;
      const optionButtons = attributeElement.querySelectorAll('.option');

      optionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const optionId = button.dataset.optionId;

          // Update aria-pressed for this attribute's buttons
          optionButtons.forEach((b) => b.setAttribute('aria-pressed', 'false'));
          button.setAttribute('aria-pressed', 'true');

          // Update selection
          attributeSelections[attributeId] = optionId;

          // Update available options for subsequent attributes
          updateAvailableOptions();

          // Update display with matching variation
          updateProductDisplay(product);

          // Update order URL
          updateOrderUrl();
        });
      });
    });
  }

  // The color swatches in the product header open the configurator on the
  // first variation in that color
  function setupColorOptionHandlers() {
    document.querySelectorAll('.product-header .color-option').forEach((button) => {
      const { attributeId, optionId } = button.dataset;
      const variation = productData.variations?.find(
        (variation) => variation[attributeId] == optionId,
      );

      if (variation?.image) {
        preloadImage(variation.image);
      }

      button.addEventListener('click', () => {
        if (variation) {
          selectVariation(variation);
        }

        document.getElementById('open-modal-configurator').click();
      });
    });
  }

  // Select all of a variation's options at once
  function selectVariation(variation) {
    attributeOrder.forEach((attributeId) => {
      attributeSelections[attributeId] = variation[attributeId];
    });

    configuratorElement.querySelectorAll('.attribute').forEach((attributeElement) => {
      const selectedOptionId = attributeSelections[attributeElement.dataset.attributeId];

      attributeElement.querySelectorAll('.option').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.optionId == selectedOptionId));
      });
    });

    updateAvailableOptions();
    updateProductDisplay(productData);
    updateOrderUrl();
  }

  // Update available options based on current selections
  function updateAvailableOptions() {
    if (!productData || !attributeOptions) {
      return;
    }

    const attributeElements = configuratorElement.querySelectorAll('.attribute');

    attributeElements.forEach((attributeElement) => {
      const attributeId = attributeElement.dataset.attributeId;
      const attributeIndex = attributeOrder.indexOf(parseInt(attributeId));
      const optionButtons = attributeElement.querySelectorAll('.option');

      // Only process options for this attribute and subsequent attributes
      // Skip if this is the first attribute (always all available)
      if (attributeIndex <= 0) {
        return;
      }

      // Get all previous attribute IDs in the order
      const previousAttributeIds = attributeOrder.slice(0, attributeIndex);

      optionButtons.forEach((button) => {
        const optionId = button.dataset.optionId;

        // Check if this option is available given the current selections
        const isAvailable = isOptionAvailable(
          parseInt(attributeId),
          parseInt(optionId),
          previousAttributeIds,
        );

        if (isAvailable) {
          button.removeAttribute('disabled');
          button.classList.remove('opacity-50');

          // Remove sold out line if it exists
          const existingSoldOutLine = button.querySelector('.sold-out-line');
          if (existingSoldOutLine) {
            button.removeChild(existingSoldOutLine);
          }
        } else {
          button.setAttribute('disabled', 'true');
          button.classList.add('opacity-50');

          // Add sold out line if it doesn't exist
          if (!button.querySelector('.sold-out-line')) {
            // Create diagonal sold out line
            const soldOutLine = document.createElement('div');
            soldOutLine.className = 'sold-out-line absolute inset-0 pointer-events-none z-10';
            soldOutLine.innerHTML = `
              <svg class="absolute inset-0 w-full h-full opacity-50" preserveAspectRatio="none">
                <line
                  x1="0"
                  y1="100%"
                  x2="100%"
                  y2="0"
                  stroke="red"
                  stroke-width="3"
                />
              </svg>
            `;

            // Add position relative if not already present
            if (!button.classList.contains('relative')) {
              button.classList.add('relative');
            }

            button.appendChild(soldOutLine);
          }

          // If this option is currently selected but not available,
          // find the first available option and select it
          if (button.getAttribute('aria-pressed') === 'true') {
            const firstAvailableOption = Array.from(optionButtons).find((opt) => {
              const optId = opt.dataset.optionId;
              return isOptionAvailable(
                parseInt(attributeId),
                parseInt(optId),
                previousAttributeIds,
              );
            });

            if (firstAvailableOption) {
              // Update selection to the first available option
              optionButtons.forEach((b) => b.setAttribute('aria-pressed', 'false'));
              firstAvailableOption.setAttribute('aria-pressed', 'true');
              attributeSelections[attributeId] = firstAvailableOption.dataset.optionId;
            }
          }
        }
      });
    });
  }

  // Check if an option is available given previous selections
  function isOptionAvailable(attributeId, optionId, previousAttributeIds) {
    // Get all variations that match our current selections for previous attributes
    const matchingVariations = productData.variations.filter((variation) => {
      return previousAttributeIds.every((prevAttrId) => {
        return variation[prevAttrId] == attributeSelections[prevAttrId];
      });
    });

    // Check if any of these variations include our option
    return matchingVariations.some((variation) => variation[attributeId] == optionId);
  }

  // Update product display based on current selections
  function updateProductDisplay(product) {
    // Find matching variation
    const matchingVariation = findMatchingVariation(product.variations, attributeSelections);

    if (matchingVariation) {
      // Update image
      if (configImage && matchingVariation.image) {
        // Change source element of parent <picture> element in case WebP Express is being used
        if (configImage.previousSibling.nodeName.toLowerCase() == 'source') {
          configImage.previousSibling.srcset = matchingVariation.image;
        }

        configImage.src = matchingVariation.image;
        configImage.alt = matchingVariation.title || '';
      }

      // Update title
      if (configTitle) {
        configTitle.textContent = matchingVariation.title || '';
      }

      // Update description
      if (configDescription) {
        configDescription.innerHTML = matchingVariation.description || '';
      }

      // Update price
      if (priceElement && (matchingVariation.price || matchingVariation.custom)) {
        if (matchingVariation.custom == true) {
          priceElement.textContent = window.js_localizations.custom_label;
        } else {
          priceElement.textContent = formatPrice(matchingVariation.price);
        }
      }
    }
  }

  // Find the variation that matches current selections
  function findMatchingVariation(variations, selections) {
    return variations.find((variation) => {
      return Object.keys(selections).every((attributeId) => {
        return variation[attributeId] == selections[attributeId];
      });
    });
  }

  // Update the order URL with current attribute selections
  function updateOrderUrl() {
    if (!orderButton) {
      return;
    }

    const baseUrl = orderButton.dataset.orderUrl;
    if (!baseUrl) {
      return;
    }

    const queryString = Object.entries(attributeSelections)
      .map(([attributeId, optionId]) => `&attribute_${attributeId}=${optionId}`)
      .join('');

    orderButton.href = baseUrl + queryString;
  }
}
